import { z } from 'zod';
import { capitalize } from '../../utils/capitalize.js';
import { optionalFormString, optionalFormTrimmedString } from '../zod-helpers/index.js';
import { ADDRESS_CONSTRAINTS } from './constraints.js';
import { addressFormPartFields, buildAddressFormSchema, EMPTY_ADDRESS_FORM, EMPTY_ADDRESS_LOCATION_FORM, } from './create-form.js';
// The base object's own keys — a sameAsField/secondaryField colliding with one of these would
// silently overwrite it via .extend() instead of erroring, so every caller is checked against it.
// Every string field the strict secondary schema requires. All of them, not just the two that were
// required before the address rule tightened -- an unnormalized undefined fails z.string()'s base
// type check before .min() runs, surfacing Zod's generic type error instead of "is required."
const SECONDARY_STRING_FIELDS = [
    'addressName',
    'address',
    'city',
    'stateId',
    'countryId',
    'zip',
];
const BASE_PAIR_FORM_KEYS = new Set([
    'typeId',
    'defaultFlag',
    'addressName',
    'address',
    'city',
    'stateId',
    'countryId',
    'zip',
]);
// Same shape, no required-field checks — used for the outer object's secondary-leg key so its
// content is only validated by buildSecondaryPairFormSchema's superRefine (conditional on the
// "same as shipping" flag), not unconditionally by the base object parse.
const lenientAddressLocationFormSchema = z.object({
    addressName: optionalFormTrimmedString(ADDRESS_CONSTRAINTS.addressName.maxLength),
    address: optionalFormTrimmedString(ADDRESS_CONSTRAINTS.address.maxLength),
    city: optionalFormTrimmedString(ADDRESS_CONSTRAINTS.city.maxLength),
    stateId: optionalFormString(),
    countryId: optionalFormString(),
    zip: optionalFormTrimmedString(ADDRESS_CONSTRAINTS.zip.maxLength),
});
export function buildSecondaryPairFormSchema(allowedTypes, secondaryFields, invalidTypeMessage = 'Invalid address type.') {
    const { sameAsField, secondaryField, requireFullAddress } = secondaryFields;
    const strictness = { requireFullAddress };
    // WHY: the secondary leg is validated by the superRefine below re-parsing this schema, so
    // strictness has to reach it too -- the base shape alone would leave billing loose.
    const secondaryLegSchema = z.object({
        addressName: z
            .string()
            .trim()
            .min(1, `${capitalize(ADDRESS_CONSTRAINTS.addressName.label.toLowerCase())} is required.`)
            .max(ADDRESS_CONSTRAINTS.addressName.maxLength),
        address: z
            .string()
            .trim()
            .min(1, `${capitalize(ADDRESS_CONSTRAINTS.address.label.toLowerCase())} is required.`)
            .max(ADDRESS_CONSTRAINTS.address.maxLength),
        ...addressFormPartFields(strictness),
    });
    if (BASE_PAIR_FORM_KEYS.has(sameAsField) || BASE_PAIR_FORM_KEYS.has(secondaryField)) {
        throw new Error(`buildSecondaryPairFormSchema: sameAsField/secondaryField must not collide with a base address field (got "${sameAsField}", "${secondaryField}")`);
    }
    return buildAddressFormSchema(allowedTypes, invalidTypeMessage, strictness)
        .extend({
        // Relaxed at the base shape — superRefine below is the sole enforcer of "required when
        // not same-as-shipping", so the base object parse doesn't reject an empty secondary
        // sub-object while same-as-shipping is true (or unset).
        [sameAsField]: z.boolean().optional(),
    })
        .extend({
        [secondaryField]: lenientAddressLocationFormSchema.optional(),
    })
        .superRefine((rawData, ctx) => {
        const data = rawData;
        if (data[sameAsField] !== false)
            return;
        // Normalize undefined -> '' before the strict re-check: RHF can leave a conditionally
        // rendered subtree's fields as undefined rather than '' depending on registration timing,
        // and undefined would otherwise fail z.string()'s base type check before .min() runs,
        // surfacing Zod's generic "expected string, received undefined" instead of "is required."
        const secondary = data[secondaryField] ?? {};
        const normalizedSecondary = {
            ...secondary,
            ...Object.fromEntries(SECONDARY_STRING_FIELDS.map((fieldName) => [fieldName, secondary[fieldName] ?? ''])),
        };
        const secondaryResult = secondaryLegSchema.safeParse(normalizedSecondary);
        if (secondaryResult.success)
            return;
        for (const issue of secondaryResult.error.issues) {
            ctx.addIssue({
                ...issue,
                path: [secondaryField, ...issue.path],
            });
        }
    });
}
export function buildEmptySecondaryPairForm(sameAsField, secondaryField) {
    return {
        ...EMPTY_ADDRESS_FORM,
        [sameAsField]: true,
        [secondaryField]: { ...EMPTY_ADDRESS_LOCATION_FORM },
    };
}
/** Billing/shipping pair form — customer's shape; also the generic cast-target type used by the shared web address components. */
export function buildBillingAndShippingPairFormSchema(allowedTypes, invalidTypeMessage = 'Invalid address type.', strictness = {}) {
    return buildSecondaryPairFormSchema(allowedTypes, { sameAsField: 'billingSameAsShipping', secondaryField: 'billing', ...strictness }, invalidTypeMessage);
}
export const EMPTY_ADDRESS_PAIR_FORM = buildEmptySecondaryPairForm('billingSameAsShipping', 'billing');
//# sourceMappingURL=create-pair-form.js.map